import {
  TMableEvent,
  TServiceMetrics,
} from '@/core/constants/event_types/base_mable_event';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { Logger } from '@/shared/logger/logger';

/**
 * Utility functions for handling service metrics in Mable events
 */
export class ServiceMetricsUtils {
  /**
   * Adds a service metric to a Mable event with the current timestamp as entrance time
   * @param mableEvent - The event to add the metric to
   * @param serviceName - The name of the service (will be prefixed with 'scriptag-')
   * @param startTime - Optional start time in ms epoch, if not provided will use current time
   * @returns The modified event with the service metric added
   */
  public static async addServiceMetric(
    mableEvent: TMableEvent,
    serviceName: string,
    startTime?: number,
  ): Promise<TMableEvent> {
    if (!mableEvent?.md) {
      return mableEvent;
    }

    try {
      const normalizedServiceName = `scriptag-${serviceName.toLowerCase()}`;
      // Always get a current timestamp for exit time
      const exitTimestamp = await TimeUtils.getCurrentEpochInMsSynced();
      // For entrance time, use provided startTime or current time if not found
      const entranceTimestamp = startTime ?? exitTimestamp;

      // Check if a metric with the same service name already exists
      if (Array.isArray(mableEvent.md.svc_metrics)) {
        const existingMetricIndex = mableEvent.md.svc_metrics.findIndex(
          metric => metric.sn === normalizedServiceName,
        );

        if (
          existingMetricIndex !== -1 &&
          mableEvent.md.svc_metrics[existingMetricIndex]
        ) {
          // Update only the exit time of the existing metric
          // Get a new timestamp for the exit time to ensure it's different from the entrance time
          const newExitTimestamp = await TimeUtils.getCurrentEpochInMsSynced();
          mableEvent.md.svc_metrics[existingMetricIndex].edt = new Date(
            newExitTimestamp,
          ).toISOString();
          return mableEvent;
        }
      }

      // Add service metrics to metadata
      const serviceMetric: TServiceMetrics = {
        eat: new Date(entranceTimestamp).toISOString(),
        edt: new Date(exitTimestamp).toISOString(),
        sn: normalizedServiceName,
      };

      // Initialize or update the service metrics array in metadata
      mableEvent.md.svc_metrics = Array.isArray(mableEvent.md.svc_metrics)
        ? [...mableEvent.md.svc_metrics, serviceMetric]
        : [serviceMetric];
    } catch (error) {
      Logger.error(`Failed to add service metric for ${serviceName}`, error);
    }

    return mableEvent;
  }

  /**
   * Updates the exit timestamp of a service metric in a Mable event
   * @param mableEvent - The event containing the service metric
   * @param serviceName - The name of the service (will be prefixed with 'scriptag-')
   * @returns The modified event with the updated exit timestamp
   */
  public static async updateServiceMetricExitTime(
    mableEvent: TMableEvent,
    serviceName: string,
  ): Promise<TMableEvent> {
    if (!mableEvent?.md?.svc_metrics) {
      return mableEvent;
    }

    try {
      const exitTimestamp = await TimeUtils.getCurrentEpochInMsSynced();

      const metricIndex = mableEvent.md.svc_metrics.findIndex(
        metric => metric.sn === `scriptag-${serviceName.toLowerCase()}`,
      );

      if (metricIndex !== -1 && mableEvent.md.svc_metrics[metricIndex]) {
        mableEvent.md.svc_metrics[metricIndex].edt = new Date(
          exitTimestamp,
        ).toISOString();
      }
    } catch (error) {
      Logger.error(
        `Failed to update service metric exit time for ${serviceName}`,
        error,
      );
    }

    return mableEvent;
  }
}
